import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { selectAuth } from '../../services/auth/selectors'
import { selectPrimaryLanguage } from '../../services/user/selectors'
import { parse as parseQuery } from '../../services/query-string/index'
import { historyRedirect, HISTORY_METHOD_REPLACE } from '../../services/navigation/index'
import { setAiGa4Event } from '../../services/event-tracking/actions'
import { setOraclePersona } from '../../services/oracle/actions'
import { AI_CONVERSATION_STARTED_GA4_EVENT } from '../../services/event-tracking/index'
import { URL_AI_CHAT } from '../../services/url/constants'
import { Location, History } from './history-types'
import { useOraclePersonalityListQuery } from './queries.generated'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'
import { getEnabledPersonalitiesWhereClause, sortPersonalitiesByDisplayPriority } from './personalityFilter'
import { OracleErrorToast } from '../OracleCommons/OracleErrorToast'

interface OracleMobilePersonalityMenuProps {
  isVisible: boolean
  onClose: () => void
  location: Location
  history: History
  triggerRef?: React.RefObject<HTMLElement>
}

export const OracleMobilePersonalityMenu: React.FC<OracleMobilePersonalityMenuProps> = ({
  isVisible,
  onClose,
  location,
  history,
  triggerRef,
}) => {
  const auth = useSelector(selectAuth)
  const language = useSelector(selectPrimaryLanguage)
  const dispatch = useDispatch()
  const menuRef = useRef<HTMLDivElement>(null)

  // Error toast state
  const [showErrorToast, setShowErrorToast] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  // Error message translations
  const personalityLoadFailedError = useTranslatedText({ keys: ['errors', 'personalityLoadFailed'] })

  const currentQuery = parseQuery(location.search)

  // Get translated text
  const selectGuideLabel = useTranslatedText({ keys: ['mobilePersonalityMenu', 'selectGuide'] })
  const sageLabel = useTranslatedText({ keys: ['pillSelector', 'sage'] })
  const architectLabel = useTranslatedText({ keys: ['pillSelector', 'architect'] })

  const personalityLabels: Record<string, string> = {
    sage: sageLabel,
    architect: architectLabel,
  }

  // Get personality data with where clause
  const {
    data: personalityData,
    error: personalityError,
  } = useOraclePersonalityListQuery({
    variables: {
      limit: 5,
      offset: 0,
      order: [['id', 'ASC']],
      where: getEnabledPersonalitiesWhereClause(),
    },
    context: {
      jwt: auth.get('jwt'),
      useMaster: true,
    },
    fetchPolicy: 'cache-first',
  })

  // Handle personality list query error with toast
  React.useEffect(() => {
    if (personalityError && !personalityData) {
      setErrorMessage(personalityLoadFailedError)
      setShowErrorToast(true)
    }
  }, [personalityError, personalityData, personalityLoadFailedError])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node
      // Don't close if clicking on the menu itself or the trigger button
      if (
        (menuRef.current && menuRef.current.contains(target))
        || (triggerRef?.current && triggerRef.current.contains(target))
      ) {
        return
      }
      onClose()
    }

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [isVisible, onClose, triggerRef])

  const filteredPersonalities = React.useMemo(() => {
    if (!personalityData?.oraclePersonalityList?.oraclePersonalities) {
      return []
    }
    return sortPersonalitiesByDisplayPriority(
      personalityData.oraclePersonalityList.oraclePersonalities,
    )
  }, [personalityData])

  const handlePersonalitySelect = (personalityKey: string) => {
    // Find personality ID from key in filtered personalities
    const selectedPersonality = filteredPersonalities.find((p) => p.key === personalityKey)

    if (selectedPersonality) {
      // Update URL with personality ID and clear chatSessionId
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { chatSessionId, ...queryWithoutChatSession } = currentQuery
      const newQuery = { ...queryWithoutChatSession, oraclePersonalityId: selectedPersonality.id }

      // Use history navigation when changing personality (removes chatSessionId)
      historyRedirect({
        history,
        url: URL_AI_CHAT,
        query: newQuery,
        auth,
        language,
        historyMethod: HISTORY_METHOD_REPLACE,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any)
    }

    // GA4 event
    dispatch(setOraclePersona(selectedPersonality.id))
    dispatch(setAiGa4Event(AI_CONVERSATION_STARTED_GA4_EVENT.mergeDeep({
      ui_element: 'Mobile Personality Menu',
    })))

    // Close the menu
    onClose()
  }

  if (!isVisible) {
    return null
  }

  return (
    <div
      ref={menuRef}
      className="oracle-mobile-personality-menu"
    >
      <div className="oracle-mobile-personality-menu__header">
        {selectGuideLabel}
      </div>
      <div className="oracle-mobile-personality-menu__items">
        {filteredPersonalities.map((personality) => (
          <button
            key={personality.id}
            className="oracle-mobile-personality-menu__item"
            onClick={() => handlePersonalitySelect(personality.key)}
            type="button"
          >
           <span className="oracle-mobile-personality-menu__label">
              {personalityLabels[personality.key] || personality.key}
            </span>
          </button>
        ))}
      </div>

      <OracleErrorToast
        isVisible={showErrorToast}
        onClose={() => {
          setShowErrorToast(false)
          setErrorMessage(undefined)
        }}
        message={errorMessage}
      />
    </div>
  )
}
